import React, { ReactNode, useState, useRef } from 'react'
import ReactDOM from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'

import cs from 'classnames'
import styled from 'styled-components'
import Button from '../../../common/button'
import IconTrash from '../../../common/icons/icon-trash'
import IconInfo from '../../../common/icons/icon-info'
import Modal, { ModalButton } from '../../../common/modal'
import ErrorModal from '../../../common/error-modal'
import SaveErrorModal from '../../../common/save-error-modal'
import KeyboardListener, {
  KeyCode,
  KeyBinding,
} from '../../../common/keyboard-listener'
import ConfirmLeaveDialog from '../../confirm-leave-dialog'
import SaveDialog from './save-dialog'
import EditableText from '../../../common/form/editable-text'
import moment from 'moment'

import type { AppDispatch } from '../../../../stores/ducks'
import * as fromScenes from '../../../../stores/ducks/scenes'
import { SavedScene } from '../../../../stores/ducks/scenes/SavedScene'
import { getSavedSceneId, getPendingExternalUpdate, getScenesList } from '../../../../stores/ducks/scenes/selectors'
import { getIsDirty } from '../../../../stores/ducks/dirty/selectors'
import { HttpError } from '../../../../../types/HttpError'

const TwoLineEllipsis = styled.p`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: normal;
  word-break: break-word;
  margin: 0;
  margin-bottom: 0.375rem;
`

const SceneInfoTooltip = ({
  updatedBy,
  updatedByName,
  updatedAt,
  isSelected,
}: {
    updatedBy: string;
    updatedByName?: string;
    updatedAt: string;
    isSelected: boolean;
}) => {
  const triggerRef = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)
  const [coords, setCoords] = useState({ top: 0, left: 0 })

  const show = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      setCoords({ top: rect.top + rect.height / 2, left: rect.right + 8 })
    }
    setVisible(true)
  }

  const hide = () => setVisible(false)
  const displayName = updatedByName || updatedBy

  return (
        <div
            ref={triggerRef}
            className={cs('pointer', {
              'c-gray-dark c-secondary-hover': !isSelected,
              'c-gray-lighter c-gray-light-hover': isSelected,
            })}
            style={{ overflow: 'visible', flexShrink: 0 }}
            onMouseEnter={show}
            onMouseLeave={hide}
        >
            <IconInfo size={16} color="currentcolor" />
            {visible && ReactDOM.createPortal(
                <div
                    role="tooltip"
                    style={{
                      position: 'fixed',
                      top: coords.top,
                      left: coords.left,
                      transform: 'translateY(-50%)',
                      zIndex: 9999,
                      background: 'var(--white)',
                      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.2)',
                      borderRadius: '2px',
                      padding: '1em',
                      fontSize: '0.8rem',
                      textAlign: 'left',
                      width: '162px',
                      boxSizing: 'border-box',
                      pointerEvents: 'none',
                      color: 'var(--todo_333)',
                    }}
                >
                    <small>Last edited by</small>
                    <TwoLineEllipsis>{displayName}</TwoLineEllipsis>
                    <time className={'truncate f7 c-gray-accessible'}>{moment(updatedAt).fromNow()}</time>
                </div>,
                document.body,
            )}
        </div>
  )
}

const DeleteSceneModalButton = ({
  sceneId,
  button,
  onRemoveScene,
}: {
    sceneId: string;
    button: (openModal: () => void) => ReactNode;
    onRemoveScene: (sceneId: string) => void;
}) => {
  const handleRemove = (closeModal: () => void) => {
    onRemoveScene(sceneId)
    closeModal()
  }

  return (
        <ModalButton
            small
            onClick={(event: React.MouseEvent) => event.stopPropagation()}
            modalContent={(closeModal: () => void) => (
                <div
                    onClick={(event) => {
                      event.stopPropagation()
                    }}
                >
                    <h2 className="mt0">Remove</h2>
                    <div className="relative">
                        <p>Are you sure you want to remove this from your saved list?</p>
                    </div>
                    <div className="flex justify-end items-end mt3">
                        <Button
                            onClick={(event) => {
                              event.stopPropagation()
                              closeModal()
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            btnType="primary"
                            className="ml2"
                            onClick={(event) => {
                              event.stopPropagation()
                              handleRemove(closeModal)
                            }}
                        >
                            Remove
                        </Button>
                    </div>
                    <KeyboardListener
                      bindings={[
                        KeyBinding(KeyCode.enter, () => handleRemove(closeModal)),
                        KeyBinding(KeyCode.esc, closeModal)
                      ]}
                    />
                </div>
            )}
            button={button}
        />
  )
}

const SceneThumbnail = ({ thumbnail = null }: { thumbnail?: string | null }) => {
  return (
    <img
      width="48px"
      height="48px"
      src={thumbnail || '/img/placeholder-material.jpg'}
    />
  )
}

const SavedSceneCardList = ({
  onRemoveScene,
  onSelectScene,
  onRenameScene,
  savedSceneId,
}: {
    onRemoveScene: (sceneId: string) => void;
    onSelectScene: (sceneId: string, sceneTitle: string) => void;
    onRenameScene: (sceneId: string, sceneTitle: string) => void;
    savedSceneId: string | null;
}) => {
  const scenesList = useSelector(getScenesList)
  const isDirty = useSelector(getIsDirty)

  const getTime = (dateStr: string) => {
    const t = new Date(dateStr).getTime()
    return isNaN(t) ? 0 : t
  }

  const orderedScenesList = [...scenesList].sort(
    (a : SavedScene, b : SavedScene) => getTime(b.updatedAt) - getTime(a.updatedAt)
  )

  return (
        <ul className="p0 my0 mxn1 f6" data-testid="saved-scene-card-list">
            {orderedScenesList.map((scene) => (
                <li
                    onClick={() => {
                      if (scene.id !== savedSceneId) { onSelectScene(scene.id, scene.title) }
                    }}
                    key={scene.id}
                    className={cs('flex items-center pointer', {
                      'c-gray-dark bg-gray-light-hover c-gray-dark-hover':
                            scene.id !== savedSceneId,
                      'bg-secondary bg-secondary-light-hover c-gray-lighter c-gray-lighter-hover':
                            scene.id === savedSceneId,
                    })}
                    style={{
                      width: '100%',
                      maxWidth: '100%',
                      minWidth: 0,
                      overflow: 'visible',
                      padding: '0.25rem 0.5rem',
                    }}
                    data-testid={`saved-scene-item-${scene.title}`}
                >
                    <div
                        className="width-100"
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '48px minmax(0, 1fr) auto auto',
                          columnGap: '0.5rem',
                          alignItems: 'center',
                          minWidth: 0,
                          overflow: 'visible',
                        }}
                    >
                        <SceneThumbnail thumbnail={scene.thumbnail} />
                        <div className="pr2" style={{ minWidth: 0, overflow: 'hidden', padding: '0.2rem 0' }}>
                            <EditableText
                                defaultValue={scene.title}
                                editable={scene.id === savedSceneId}
                                showIcon={false}
                                className="truncate f6"
                                activeClassName={cs('f6', {
                                  'c-gray-lighter': scene.id === savedSceneId,
                                })}
                                style={{ display: 'block', paddingLeft: 0 }}
                                onClick={(event) => event.stopPropagation()}
                                onChange={(nextTitle) => {
                                  if (nextTitle !== scene.title) {
                                    onRenameScene(scene.id, nextTitle)
                                  }
                                }}
                            >
                                {(value, textProps) => (
                                    <span {...textProps}>
                                        {isDirty && scene.id === savedSceneId ? '* ' : ''}{value}
                                    </span>
                                )}
                            </EditableText>
                        </div>
                        <SceneInfoTooltip
                            updatedBy={scene.updatedBy}
                            updatedByName={scene.updatedByName}
                            updatedAt={scene.updatedAt}
                            isSelected={scene.id === savedSceneId}
                        />
                        <DeleteSceneModalButton
                            onRemoveScene={onRemoveScene}
                            button={(openModal: () => void) => (
                                <div
                                    data-testid="delete-button"
                                    className={cs('pointer', {
                                      'c-gray-dark c-secondary-hover': scene.id !== savedSceneId,
                                      'c-gray-lighter c-gray-light-hover': scene.id === savedSceneId,
                                    })}
                                    style={{ flexShrink: 0 }}
                                    onClick={(event) => {
                                      event.stopPropagation()
                                      openModal()
                                    }}
                                >
                                    <IconTrash
                                        className="ml1"
                                        color="currentcolor"
                                    />
                                </div>
                            )}
                            sceneId={scene.id}
                        />
                    </div>
                </li>
            ))}
        </ul>
  )
}

export const SavedSceneList = () => {
  const dispatch = useDispatch<AppDispatch>()
  const savedSceneId = useSelector(getSavedSceneId)
  const isDirty = useSelector(getIsDirty)
  const pendingExternalUpdate = useSelector(getPendingExternalUpdate)

  const [nextScene, setNextScene] = useState<{ id: string; title: string } | null>(null)
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false)
  const [saveMessage, setSaveMessage] = useState('')
  const [placeholderTitle, setPlaceholderTitle] = useState('')

  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false)
  const [errorTitle, setErrorTitle] = useState('')
  const [errorDescription, setErrorDescription] = useState('')
  const [isSaveErrorModalOpen, setIsSaveErrorModalOpen] = useState(false)

  const setErrorModal = (title: string, description: string) => {
    setErrorTitle(title)
    setErrorDescription(description)
    setIsErrorModalOpen(true)
  }

  const handleRemoveScene = async (sceneId: string) => {
    try {
      await dispatch(fromScenes.deleteSavedScene(sceneId))
    } catch {
      setErrorModal('Remove failed', 'Unable to remove this save. Please try again.')
    }
  }

  const handleSelectScene = (sceneId: string, sceneTitle: string) => {
    if (isDirty) {
      setNextScene({ id: sceneId, title: sceneTitle })
    } else {
      switchScene(sceneId)
    }
  }

  const handleSaveChanges = async () => {
    if (!savedSceneId) {
      setIsSaveDialogOpen(true)
      return
    }

    if (pendingExternalUpdate) {
      setSaveMessage('This save was updated by another user. To keep your changes, save as a new copy.')
      const scenesList = useSelector(getScenesList)
      const currentScene = scenesList.find((scene: SavedScene) => scene.id === savedSceneId)
      setPlaceholderTitle(`${currentScene?.title} copy`)
      setIsSaveDialogOpen(true)
      return
    }

    try {
      await dispatch(fromScenes.saveOrUpdateScene(savedSceneId))
      if (nextScene) { switchScene(nextScene.id) }
    } catch {
      setIsSaveErrorModalOpen(true)
    }
  }

  const handleSaveDialogSave = () => {
    setIsSaveDialogOpen(false)
    setSaveMessage('')
    setPlaceholderTitle('')
    if (nextScene) { switchScene(nextScene.id) }
  }

  const handleSaveDialogClose = () => {
    setIsSaveDialogOpen(false)
    setNextScene(null)
  }

  const handleDiscardChanges = () => {
    if (!nextScene) return
    switchScene(nextScene.id)
  }

  const switchScene = async (sceneId: string) => {
    try {
      dispatch(fromScenes.applyPendingExternalChanges())
      await dispatch(fromScenes.fetchAndLoadSavedScene(sceneId))
    } catch {
      setErrorModal('Load failed', "This save couldn't be loaded. Please try again.")
    }
    setNextScene(null)
  }

  const handleCancelSwitchScene = () => {
    setNextScene(null)
  }

  const handleRenameScene = async (sceneId: string, sceneTitle: string) => {
    try {
      await dispatch(fromScenes.renameSavedScene(sceneId, sceneTitle))
    } catch (err) {
      if (err instanceof HttpError && err.status === 404) {
        setErrorModal('Rename failed', 'This save has been deleted by another user. Save your progress to rename it.')
        if (sceneId === savedSceneId) {
          dispatch(fromScenes.resetCurrentSavedSceneToUnsaved())
          dispatch(fromScenes.applyPendingExternalChanges())
        }
      } else {
        setErrorModal('Rename failed', "Your changes couldn't be saved. Please try again.")
      }
    }
  }

  return (
    <>
      <div style={{ marginTop: '1rem' }}>
        <SavedSceneCardList
          onRemoveScene={handleRemoveScene}
          onSelectScene={handleSelectScene}
          onRenameScene={handleRenameScene}
          savedSceneId={savedSceneId}
        />
      </div>
      <Modal
        isOpen={nextScene !== null && nextScene.id !== savedSceneId && !isSaveDialogOpen}
        onRequestClose={handleCancelSwitchScene}
        width={600}
      >
        <ConfirmLeaveDialog
          title="Unsaved changes"
          onSave={handleSaveChanges}
          onDiscard={handleDiscardChanges}
          onClose={handleCancelSwitchScene}
        />
      </Modal>
      <Modal
        isOpen={isSaveDialogOpen}
        onRequestClose={handleSaveDialogClose}
        width={600}
      >
        <SaveDialog
          onSave={handleSaveDialogSave}
          onClose={handleSaveDialogClose}
          message={saveMessage}
          placeholderTitle={placeholderTitle}
        />
      </Modal>
      <SaveErrorModal
        isOpen={isSaveErrorModalOpen}
        handleClose={() => setIsSaveErrorModalOpen(false)}
      />
      <ErrorModal
        isOpen={isErrorModalOpen}
        title={errorTitle}
        description={errorDescription}
        handleClose={() => setIsErrorModalOpen(false)}
      />
    </>
  )
}
